import { apiClient } from './api';
import {
  ShopDeliveryOffer,
  ShopDeliveryOfferCreate,
  CalculateOfferRequest,
  CalculateOfferResponse,
} from '@/types';

/**
 * Shop Delivery Offers API Service
 * Handles CRUD operations for conditional delivery offers
 * (e.g., "Free delivery on orders over 5000 tk", "10% off on 3+ items")
 */
export const shopDeliveryOfferApi = {
  /**
   * Get all delivery offers for a shop
   * Returns active offers sorted by priority
   */
  getShopOffers: (shopId: number, activeOnly: boolean = true) => {
    const params = activeOnly ? { active_only: true } : {};
    return apiClient.get<ShopDeliveryOffer[]>(`/shops/${shopId}/delivery-offers`, { params });
  },

  /**
   * Get a specific delivery offer by ID
   */
  getOfferById: (shopId: number, offerId: number) =>
    apiClient.get<ShopDeliveryOffer>(`/shops/${shopId}/delivery-offers/${offerId}`),

  /**
   * Create a new delivery offer for a shop
   * Requires seller to own the shop
   */
  createOffer: (shopId: number, data: ShopDeliveryOfferCreate) =>
    apiClient.post<ShopDeliveryOffer>(`/shops/${shopId}/delivery-offers`, data),

  /**
   * Update an existing delivery offer
   */
  updateOffer: (shopId: number, offerId: number, data: Partial<ShopDeliveryOfferCreate>) =>
    apiClient.put<ShopDeliveryOffer>(`/shops/${shopId}/delivery-offers/${offerId}`, data),

  /**
   * Delete a delivery offer (soft delete - sets is_active to false)
   */
  deleteOffer: (shopId: number, offerId: number) =>
    apiClient.delete(`/shops/${shopId}/delivery-offers/${offerId}`),

  /**
   * Calculate the best applicable offer for a given order
   * Used in cart/checkout to show potential delivery discounts
   *
   * @param shopId - The shop ID
   * @param request - Contains order_value and quantity
   * @param originalDeliveryCharge - Original delivery charge before discount
   * @returns The best applicable offer with discount details
   */
  calculateApplicableOffer: (
    shopId: number,
    request: CalculateOfferRequest,
    originalDeliveryCharge: number
  ) => {
    const params = {
      order_value: request.order_value,
      quantity: request.quantity,
      original_delivery_charge: originalDeliveryCharge,
    };
    return apiClient.post<CalculateOfferResponse>(
      `/shops/${shopId}/delivery-offers/calculate`,
      null,
      { params }
    );
  },

  /**
   * Toggle offer active status
   */
  toggleOfferStatus: (shopId: number, offerId: number, isActive: boolean) =>
    apiClient.put<ShopDeliveryOffer>(`/shops/${shopId}/delivery-offers/${offerId}`, {
      is_active: isActive,
    }),

  /**
   * Update offer priority
   */
  updateOfferPriority: (shopId: number, offerId: number, priority: number) =>
    apiClient.put<ShopDeliveryOffer>(`/shops/${shopId}/delivery-offers/${offerId}`, {
      priority,
    }),
};
